import type { Level } from '@arrow/puzzle-schema';

import { apiUrl, isPermanentFailure, postJson, type WriteResult } from '../api/client.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { APP_VERSION, browserBeacon, isAnalyticsEnabled, platformKeys, platformStore, usageCountsOn } from './config.ts';
import { getDeviceId } from './identity.ts';
import { isNestMember } from '../nests/store.ts';

/**
 * Solve sync: an anonymous copy of each playthrough goes to `POST /v1/solves` (an
 * upsert) when it starts and when it finishes. Local play stays the source of truth;
 * this never blocks or throws. Every solve is written to a pending list first and
 * removed once the api has it, so a failed or interrupted post is retried (on the next
 * page load, when the browser comes back online, and on a timer).
 *
 * With Settings → "Count my plays" off (platform/consent.ts), a solve goes out only when
 * a challenge needs it: one played from a challenge (its creator sees how it went), or
 * the solve a player makes a challenge from (`pushSolveFor`, at "Challenge a friend"),
 * and every daily of a nest member (their nest sees those times: docs/NESTS.md, and the
 * privacy page says so). Everything else stays on the device.
 */

export type SolveStatus = 'started' | 'completed' | 'revealed';

/** The body of `POST /v1/solves`, minus `deviceId` (added here). */
export interface SolveUpload {
	puzzleId: string;
	kind: 'daily' | 'practice';
	/** Daily: the daily's level. Practice: leave it out (the api uses the puzzle's own). */
	level?: Level;
	/** Daily only: the daily's date (`YYYY-MM-DD`). */
	dailyDate?: string;
	/** Daily only. */
	dailyNumber?: number;
	status: SolveStatus;
	/** ISO 8601. */
	startedAt: string;
	completedAt?: string;
	durationMs?: number;
	checksUsed: number;
	revealsUsed: number;
	/** Filled cells a Check flagged wrong. */
	mistakes: number;
	bestFlow: number;
	/** Completed on its own local day (counts for the streak). Dailies only. */
	onTime?: boolean;
	/** How each cell fell (`encodeOutcomes`), on a finished solve. ≤ 400 chars. */
	cells?: string;
	/** Elapsed ms at each answer solve, ascending (`paceOffsets`), on a finished solve. ≤ 200 entries. */
	paceMs?: number[];
	/** The challenge this solve was played from (`/c/:code`). */
	challengeCode?: string;
	clientVersion?: string;
}

const MAX_PENDING = 50;
export const RETRY_INTERVAL_MS = 60_000;

const keyOf = (s: SolveUpload) => [s.kind, s.puzzleId, s.dailyDate ?? '', s.level ?? ''].join('|');
const finished = (s: SolveUpload) => s.status !== 'started';

function isUpload(value: unknown): value is SolveUpload {
	if (typeof value !== 'object' || value === null) return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.puzzleId === 'string' &&
		(v.kind === 'daily' || v.kind === 'practice') &&
		(v.status === 'started' || v.status === 'completed' || v.status === 'revealed') &&
		typeof v.startedAt === 'string'
	);
}

export interface SolveSyncDeps {
	store: KeyValueStore;
	enabled: () => boolean;
	/** Whether this solve may be sent on its own (default: all). Held ones wait for `retry(match)`. */
	shouldSend?: (solve: SolveUpload) => boolean;
	deviceId: () => string;
	post: (body: unknown, keepalive: boolean) => Promise<WriteResult>;
	beacon: ((url: string, data: Blob) => boolean) | null;
	solvesUrl: string;
}

export interface SolveSync {
	/** Upload one solve (queued first, so it's retried if this fails). Never rejects. */
	sync(solve: SolveUpload): Promise<void>;
	/**
	 * Retry everything pending that may be sent. With `match`, send just the pending
	 * solves it picks, held or not (a challenge being made). Never rejects.
	 */
	retry(match?: (solve: SolveUpload) => boolean): Promise<void>;
	/** Drop pending solves that `keep` rejects (the analytics choice changed). */
	prune(keep: (solve: SolveUpload) => boolean): void;
	/** Beacon everything pending (page hide). They stay pending until a fetch confirms them. */
	flushOnHide(): void;
	pending(): SolveUpload[];
	start(): void;
	stop(): void;
}

export function createSolveSync(deps: SolveSyncDeps): SolveSync {
	let memory: Record<string, SolveUpload> | null = null;
	let timer: ReturnType<typeof setInterval> | null = null;
	let retrying = false;
	const listeners: Array<() => void> = [];

	const read = (): Record<string, SolveUpload> => {
		if (memory) return { ...memory };
		try {
			const parsed = JSON.parse(deps.store.get(platformKeys.pendingSolves) ?? '{}') as unknown;
			if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
			return Object.fromEntries(Object.entries(parsed).filter(([, v]) => isUpload(v))) as Record<string, SolveUpload>;
		} catch {
			return {};
		}
	};
	const write = (pending: Record<string, SolveUpload>) => {
		const entries = Object.entries(pending).slice(-MAX_PENDING);
		const trimmed = Object.fromEntries(entries);
		if (memory) {
			memory = trimmed;
			return;
		}
		if (entries.length === 0) deps.store.remove(platformKeys.pendingSolves);
		else if (!deps.store.set(platformKeys.pendingSolves, JSON.stringify(trimmed))) memory = trimmed;
	};

	/** Adds or replaces a pending solve. A finished one is never replaced by a started one. */
	const enqueue = (solve: SolveUpload) => {
		const pending = read();
		const key = keyOf(solve);
		const existing = pending[key];
		if (existing && finished(existing) && !finished(solve)) return existing;
		delete pending[key];
		pending[key] = solve;
		write(pending);
		return solve;
	};
	/** Removes a pending solve once the api has it, unless a newer one replaced it meanwhile. */
	const settle = (solve: SolveUpload) => {
		const pending = read();
		const key = keyOf(solve);
		if (pending[key] && JSON.stringify(pending[key]) === JSON.stringify(solve)) {
			delete pending[key];
			write(pending);
		}
	};
	const send = async (solve: SolveUpload, keepalive = false): Promise<boolean> => {
		const result = await deps
			.post({ deviceId: deps.deviceId(), ...solve }, keepalive)
			.catch((): WriteResult => ({ ok: false, status: 0 }));
		// Done when stored, or when the api says it never will be (e.g. a dev puzzle it doesn't know).
		if (result.ok || isPermanentFailure(result)) settle(solve);
		return result.ok || isPermanentFailure(result);
	};

	const may = (solve: SolveUpload) => deps.shouldSend?.(solve) ?? true;
	const sync: SolveSync = {
		async sync(solve) {
			try {
				if (!deps.enabled()) return;
				const queued = enqueue({ ...solve, clientVersion: solve.clientVersion ?? APP_VERSION });
				if (may(queued)) await send(queued);
			} catch {
				// Never rejects.
			}
		},

		async retry(match) {
			if (retrying && !match) return;
			if (!match) retrying = true;
			try {
				if (!deps.enabled()) return;
				for (const solve of Object.values(read()).filter(match ?? may)) {
					if (!(await send(solve))) return; // Still offline: try again later.
				}
			} catch {
				// Never rejects.
			} finally {
				if (!match) retrying = false;
			}
		},

		prune(keep) {
			try {
				const pending = read();
				for (const [key, solve] of Object.entries(pending)) if (!keep(solve)) delete pending[key];
				write(pending);
			} catch {
				// Never throws.
			}
		},

		flushOnHide() {
			try {
				if (!deps.enabled() || !deps.beacon) return;
				for (const solve of Object.values(read()).filter(may)) {
					const json = JSON.stringify({ deviceId: deps.deviceId(), ...solve });
					deps.beacon(deps.solvesUrl, new Blob([json], { type: 'text/plain' }));
				}
			} catch {
				// Never throws.
			}
		},

		pending: () => Object.values(read()),

		start() {
			if (timer !== null || typeof window === 'undefined') return;
			timer = setInterval(() => {
				if (Object.keys(read()).length > 0) void sync.retry();
			}, RETRY_INTERVAL_MS);
			const onOnline = () => void sync.retry();
			const onVisibility = () => {
				if (document.visibilityState === 'hidden') sync.flushOnHide();
			};
			window.addEventListener('online', onOnline);
			document.addEventListener('visibilitychange', onVisibility);
			listeners.push(
				() => window.removeEventListener('online', onOnline),
				() => document.removeEventListener('visibilitychange', onVisibility),
			);
		},

		stop() {
			if (timer !== null) clearInterval(timer);
			timer = null;
			for (const off of listeners.splice(0)) off();
		},
	};
	return sync;
}

export const solveSync: SolveSync = createSolveSync({
	store: platformStore,
	enabled: () => isAnalyticsEnabled(platformStore),
	shouldSend: (solve) => sendsAlone(solve),
	deviceId: () => getDeviceId(platformStore),
	post: (body, keepalive) => postJson('/v1/solves', body, { keepalive }),
	beacon: browserBeacon(),
	solvesUrl: apiUrl('/v1/solves'),
});

/** Upload a solve (start or finish). Fire-and-forget: never throws, never blocks play. */
export function syncSolve(solve: SolveUpload): Promise<void> {
	if (!isAnalyticsEnabled(platformStore)) return Promise.resolve();
	solveSync.start();
	return solveSync.sync(solve);
}

/**
 * Whether a solve goes out on its own: always with "Count my plays" on; with it off, one a
 * challenge needs, or a daily of a nest member (their nest sees it: docs/NESTS.md).
 */
export function sendsAlone(solve: SolveUpload, store: KeyValueStore = platformStore): boolean {
	return usageCountsOn(store) || needsSending(solve) || (solve.kind === 'daily' && isNestMember(store));
}

/** A solve a challenge needs whatever the analytics choice: one played from a challenge. */
export function needsSending(solve: SolveUpload): boolean {
	return typeof solve.challengeCode === 'string' && solve.challengeCode.length > 0;
}

/**
 * Send the finished daily (`number`, `level`) now, even with "Count my plays" off: the
 * player is making a challenge of it, and a challenge is built from the api's copy.
 */
export function pushSolveFor(daily: { number: number; level: Level }): Promise<void> {
	return solveSync.retry((s) => s.kind === 'daily' && s.dailyNumber === daily.number && s.level === daily.level && s.status !== 'started');
}

/**
 * Send a daily's pending solve now, whatever "Count my plays" says, if this device is in a
 * nest: the poster's nest line wants the api to have the finish before it asks for the board.
 */
export function pushDailyForNest(daily: { number: number; level: Level }): Promise<void> {
	if (!isNestMember(platformStore)) return Promise.resolve();
	return solveSync.retry((s) => s.kind === 'daily' && s.dailyNumber === daily.number && s.level === daily.level);
}

/** Retry solves that didn't reach the api earlier. `initPlatform` calls it on boot. */
export function retryPendingSolves(): Promise<void> {
	return solveSync.retry();
}
